import axiosClient from '@/core/api/axiosClient';

/**
 * Información de otro dispositivo con sesión activa
 */
export interface DispositivoInfo {
    nombre: string;
    navegador: string;
    ultimaActividad: Date;
}

/**
 * Response del heartbeat
 */
export interface HeartbeatResponse {
    sesionActiva: boolean;
    hayOtrasSesiones: boolean;
    otroDispositivo?: DispositivoInfo;
}

/**
 * Params para registrar heartbeat
 */
export interface HeartbeatParams {
    dispositivoId: string;
    navegador: string;
    sistemaOperativo: string;
    tipoDispositivo: 'desktop' | 'mobile' | 'tablet';
    modelo: string;
}

/**
 * Configuración de sesiones desde backend
 */
export interface ConfiguracionSesion {
    permitirMultipleSesion: boolean;
    intervaloHeartbeatMs: number;
    timeoutInactividadMs: number;
}

/**
 * ApiResponse genérica del backend
 */
export interface ApiResponse<T> {
    data?: T;
    message?: string;
    success: boolean;
    errors?: string[];
}

/**
 * Client SDK: Gestión de sesiones activas
 */
export const sesionesClient = {
    /**
     * Registrar heartbeat y verificar sesiones duplicadas
     * Frontend obtiene el intervalo dinámicamente del backend (IntervaloHeartbeatSegundos)
     */
    registrarHeartbeat: async (params: HeartbeatParams): Promise<HeartbeatResponse> => {
        const response = await axiosClient.post<{ data: HeartbeatResponse }>('/Administracion/Sesiones/heartbeat', {
            DispositivoId: params.dispositivoId,
            Navegador: params.navegador,
            SistemaOperativo: params.sistemaOperativo,
            TipoDispositivo: params.tipoDispositivo,
            Modelo: params.modelo,
        });

        // Parsear ultimaActividad string ISO a Date
        const data = response.data.data;
        if (data.otroDispositivo) {
            data.otroDispositivo.ultimaActividad = new Date(data.otroDispositivo.ultimaActividad);
        }

        return data;
    },

    /**
     * Cerrar otras sesiones del usuario (mantener solo la actual)
     */
    cerrarOtrasSesiones: async (dispositivoIdActual: string): Promise<void> => {
        await axiosClient.post('/Administracion/Sesiones/cerrar-otras', {
            DispositivoIdActual: dispositivoIdActual,
        });
    },

    /**
     * Obtener configuración de sesiones desde el backend
     */
    obtenerConfiguracion: async (): Promise<ApiResponse<ConfiguracionSesion>> => {
        const response = await axiosClient.get<ApiResponse<ConfiguracionSesion>>('/Administracion/Sesiones/configuracion');
        return response.data;
    },
};
